import {firestore, functions, httpsCallable} from "@/firebase/firebaseConfig";
import {
    doc,
    getDoc,
    query,
    collection,
    where,
    getDocs,
    updateDoc,
    arrayUnion,
    FieldValue,
    serverTimestamp,
    deleteField,
    UpdateData,
    DocumentData
} from "firebase/firestore";
import {currentUser} from "@/firebase/firebaseAuth";
import {clearEnsureUserProfileCache, rememberEnsureOnboarding} from "@/lib/UserProfileEnsure";
import {updateProfile} from "@firebase/auth";
import {isTranslationReturnTo} from "@/lib/AuthReturnTo";
import {withFirestoreAuthRetry} from "@/lib/firestoreAuthRead";

const PENDING_AUTH_RETURN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface UserProfile {
    uid: string;
    displayName?: string;
    firstName?: string;
    surname?: string;
    email?: string;
    emailVerified?: boolean;
    photoURL?: string;
    referralMethod?: string;
    dateCreated?: Date;
    lastUpdated?: Date;
    onboardEditScreen?: boolean;
    onboardDashboard?: boolean;
    onboardPricing?: boolean;
    englishFlagSelection?: string;
    youtubeTermsAccepted?: boolean;
    onboardConnect?: boolean;
    onboardPlayer?: boolean;
    preferredLocale?: string;
    /** @deprecated Prefer favoriteSourceLanguages. Kept as first favourite for older clients. */
    favoriteSourceLanguage?: string;
    /** Platform favourites for content/source language pickers (glossary, etc.). Base codes e.g. ["en","es"]. */
    favoriteSourceLanguages?: string[];
    deletionStatus?: 'pending' | 'in_progress' | 'failed';
    userIntents?: Record<string, boolean>;
    signupIntent?: "translator";
    pendingAuthReturnTo?: string;
    pendingAuthReturnToCreatedAt?: Date;
    pendingAuthReturnToConsumedAt?: Date;
    lastAuthReturnTo?: string;
    createdFromTranslationInvite?: boolean;
    visitDates?: string[];
    discountOfferStatus?: 'offered' | 'accepted' | 'dismissed';
    discountOfferAcceptedAt?: Date;
    attribution?: {
        ref_page?: string;
        ft_source?: string;
        ft_medium?: string;
        ft_campaign?: string;
        ft_landing?: string;
    };
}

export interface FirestoreUserProfile {
    displayName?: string;
    firstName?: string;
    surname?: string;
    email?: string;
    photoURL?: string;
    referralMethod?: string;
    onboardEditScreen?: boolean;
    onboardDashboard?: boolean;
    onboardPricing?: boolean;
    onboardConnect?: boolean;
    onboardPlayer?: boolean;
    preferredLocale?: string;
    favoriteSourceLanguage?: string;
    favoriteSourceLanguages?: string[];
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export interface OnboardingPatch {
    onboardEditScreen?: boolean;
    onboardDashboard?: boolean;
    onboardPricing?: boolean;
    onboardConnect?: boolean;
    onboardPlayer?: boolean;
    youtubeTermsAccepted?: boolean;
}

export interface PreferencesPatch {
    preferredLocale?: string;
    englishFlagSelection?: string;
    favoriteSourceLanguage?: string | null;
    favoriteSourceLanguages?: string[] | null;
}

export interface IdentityPatch {
    displayName?: string;
    email?: string;
    photoURL?: string;
    referralMethod?: string;
}

/**
 * Get the UserProfile from Firestore. Does not include any data from the AuthUser object
 * See getCombinedUserProfile for a version that combines the AuthUser and FirestoreUserProfile
 * @param uid
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
    try {
        return await withFirestoreAuthRetry(uid, async () => {
            const snapshot = await getDoc(doc(firestore, "UserProfiles", uid));
            if (!snapshot.exists()) {
                return null;
            }
            return mapToLocalUserProfile(uid, snapshot.data());
        }, {getCurrentUser: currentUser});
    } catch (error) {
        console.error("Error getting user profile:", error);
        throw error;
    }
}

/**
 * Update the UserProfile in Firestore. Does not include any data from the AuthUser object
 * See setCombinedUserProfile for a version that combines the AuthUser and FirestoreUserProfile
 * @param profile
 */
export const updateUserProfile = async (profile: UserProfile): Promise<UserProfile> => {
    const uid = profile.uid
    const payload = toFirestorePatch(profile)

    if (!uid) {
        return Promise.reject(new Error("UserProfile uid is required"));
    }

    return updateDoc(doc(firestore, 'UserProfiles', uid), payload)
        .then(() => profile)
        .catch((error) => {
            console.error("Error updating user profile:", error);
            throw error;
        });
}

function normalizeFavoriteSourceLanguages(codes: string[] | null | undefined): string[] {
    if (!codes?.length) return [];
    const seen = new Set<string>();
    const normalized: string[] = [];
    codes.forEach((code) => {
        const next = typeof code === "string" ? code.trim() : "";
        if (!next || seen.has(next)) return;
        seen.add(next);
        normalized.push(next);
    });
    return normalized;
}

function toFirestorePatch(
    profile: Partial<UserProfile> & {
        favoriteSourceLanguage?: string | null;
        favoriteSourceLanguages?: string[] | null;
    },
): UpdateData<DocumentData> {
    const patch: UpdateData<DocumentData> = {
        lastUpdated: serverTimestamp(),
    };

    if (profile.displayName !== undefined) {
        const displayName = profile.displayName.trim();
        if (displayName) {
            patch.displayName = displayName;
            const nameParts = displayName.split(" ").filter(Boolean);
            if (nameParts.length === 1) {
                patch.firstName = nameParts[0];
            } else if (nameParts.length > 1) {
                patch.firstName = nameParts[0];
                patch.surname = displayName.replace(nameParts[0], "").trim();
            }
        }
    }

    if (profile.email !== undefined) {
        const email = profile.email.trim().toLowerCase();
        if (email) {
            patch.email = email;
        }
    }

    if (profile.photoURL !== undefined) {
        const photoURL = profile.photoURL.trim();
        if (photoURL) {
            patch.photoURL = photoURL;
        }
    }

    if (profile.referralMethod !== undefined) {
        const referralMethod = profile.referralMethod.trim();
        if (referralMethod) {
            patch.referralMethod = referralMethod;
        }
    }

    if (profile.preferredLocale !== undefined) {
        const preferredLocale = profile.preferredLocale.trim();
        if (preferredLocale) {
            patch.preferredLocale = preferredLocale;
        }
    }

    if (profile.favoriteSourceLanguages !== undefined) {
        const favoriteSourceLanguages = normalizeFavoriteSourceLanguages(profile.favoriteSourceLanguages);
        if (favoriteSourceLanguages.length) {
            patch.favoriteSourceLanguages = favoriteSourceLanguages;
            patch.favoriteSourceLanguage = favoriteSourceLanguages[0];
        } else {
            patch.favoriteSourceLanguages = deleteField();
            patch.favoriteSourceLanguage = deleteField();
        }
    } else if (profile.favoriteSourceLanguage !== undefined) {
        const favoriteSourceLanguage = typeof profile.favoriteSourceLanguage === "string"
            ? profile.favoriteSourceLanguage.trim()
            : "";
        if (favoriteSourceLanguage) {
            patch.favoriteSourceLanguage = favoriteSourceLanguage;
            patch.favoriteSourceLanguages = [favoriteSourceLanguage];
        } else {
            patch.favoriteSourceLanguage = deleteField();
            patch.favoriteSourceLanguages = deleteField();
        }
    }

    if (profile.onboardEditScreen !== undefined) patch.onboardEditScreen = profile.onboardEditScreen;
    if (profile.onboardDashboard !== undefined) patch.onboardDashboard = profile.onboardDashboard;
    if (profile.onboardPricing !== undefined) patch.onboardPricing = profile.onboardPricing;
    if (profile.onboardConnect !== undefined) patch.onboardConnect = profile.onboardConnect;
    if (profile.onboardPlayer !== undefined) patch.onboardPlayer = profile.onboardPlayer;
    if (profile.youtubeTermsAccepted !== undefined) patch.youtubeTermsAccepted = profile.youtubeTermsAccepted;
    if (profile.englishFlagSelection !== undefined) patch.englishFlagSelection = profile.englishFlagSelection;

    return patch;
}

function mapToLocalUserProfile(uid: string, doc: any): UserProfile {
    return {
        uid: uid,
        displayName: doc.displayName,
        firstName: doc.firstName,
        surname: doc.surname,
        email: doc.email,
        emailVerified: doc.emailVerified,
        photoURL: doc.photoURL,
        referralMethod: doc.referralMethod,
        dateCreated: doc.dateCreated ? doc.dateCreated.toDate() : new Date(),
        lastUpdated: doc.lastUpdated ? doc.lastUpdated.toDate() : new Date(),
        onboardEditScreen: doc.onboardEditScreen,
        onboardDashboard: doc.onboardDashboard,
        onboardPricing: doc.onboardPricing,
        englishFlagSelection: doc.englishFlagSelection,
        youtubeTermsAccepted: doc.youtubeTermsAccepted, // Fixed typo: was "youtuberTermsAccepted"
        onboardConnect: doc.onboardConnect,
        onboardPlayer: doc.onboardPlayer,
        preferredLocale: doc.preferredLocale,
        favoriteSourceLanguage: doc.favoriteSourceLanguage,
        favoriteSourceLanguages: (() => {
            const fromArray = normalizeFavoriteSourceLanguages(doc.favoriteSourceLanguages);
            if (fromArray.length) return fromArray;
            const legacy = typeof doc.favoriteSourceLanguage === "string"
                ? doc.favoriteSourceLanguage.trim()
                : "";
            return legacy ? [legacy] : undefined;
        })(),
        deletionStatus: doc.deletionStatus,
        userIntents: doc.userIntents,
        signupIntent: doc.signupIntent,
        pendingAuthReturnTo: doc.pendingAuthReturnTo,
        pendingAuthReturnToCreatedAt: doc.pendingAuthReturnToCreatedAt?.toDate?.() || undefined,
        pendingAuthReturnToConsumedAt: doc.pendingAuthReturnToConsumedAt?.toDate?.() || undefined,
        lastAuthReturnTo: doc.lastAuthReturnTo,
        createdFromTranslationInvite: doc.createdFromTranslationInvite,
        visitDates: doc.visitDates || [],
        discountOfferStatus: doc.discountOfferStatus,
        discountOfferAcceptedAt: doc.discountOfferAcceptedAt?.toDate?.() || undefined,
        attribution: doc.attribution,
    }
}

export function getConsumablePendingTranslatorReturnTo(profile: UserProfile | null | undefined): string | null {
    if (!profile?.pendingAuthReturnTo || profile.signupIntent !== "translator") {
        return null;
    }
    if (profile.pendingAuthReturnToConsumedAt) {
        return null;
    }
    if (!isTranslationReturnTo(profile.pendingAuthReturnTo)) {
        return null;
    }
    if (profile.pendingAuthReturnToCreatedAt) {
        const ageMs = Date.now() - profile.pendingAuthReturnToCreatedAt.getTime();
        if (ageMs > PENDING_AUTH_RETURN_TTL_MS) {
            return null;
        }
    }
    return profile.pendingAuthReturnTo;
}

export async function persistPendingTranslatorReturnTo(uid: string, returnTo: string): Promise<void> {
    if (!isTranslationReturnTo(returnTo)) {
        return;
    }
    await updateDoc(doc(firestore, "UserProfiles", uid), {
        signupIntent: "translator",
        pendingAuthReturnTo: returnTo,
        pendingAuthReturnToCreatedAt: serverTimestamp(),
        pendingAuthReturnToConsumedAt: deleteField(),
        createdFromTranslationInvite: true,
        lastUpdated: serverTimestamp(),
    });
}

export async function consumePendingTranslatorReturnTo(uid: string, returnTo: string): Promise<void> {
    await updateDoc(doc(firestore, "UserProfiles", uid), {
        pendingAuthReturnTo: deleteField(),
        signupIntent: deleteField(),
        pendingAuthReturnToCreatedAt: deleteField(),
        pendingAuthReturnToConsumedAt: serverTimestamp(),
        lastAuthReturnTo: returnTo,
        lastUpdated: serverTimestamp(),
    });
}

export type OnboardingIntentKey = "translation" | "shorts" | "thumbnails" | "youtube" | "hosted";

export type OnboardingIntentSelection = Record<OnboardingIntentKey, boolean>;

export interface SetOnboardingIntentsResponse {
    uid: string;
    userIntents: Record<string, boolean>;
    onboardConnect: boolean;
    onboardPlayer: boolean;
}

/**
 * Persists the user's onboarding intent selection via the
 * `user-setOnboardingIntents` callable. The server maps UI keys to canonical
 * product intent ids (for example `youtube` -> `braiv-connect`) and writes
 * them to `UserProfiles/{uid}.userIntents`, alongside the existing
 * `onboardConnect` / `onboardPlayer` routing booleans.
 */
export async function setOnboardingIntents(
    selection: OnboardingIntentSelection
): Promise<SetOnboardingIntentsResponse> {
    const callable = httpsCallable<OnboardingIntentSelection, SetOnboardingIntentsResponse>(
        functions,
        "user-setOnboardingIntents"
    );
    const result = await callable(selection);
    const uid = result.data.uid || currentUser()?.uid;
    if (uid) {
        clearEnsureUserProfileCache(uid);
    }
    return result.data;
}

/**
 * Gets a version of the user profile that combines the AuthUser and FirestoreUserProfile into one object
 * @param uid
 */
export const getCombinedUserProfile = async (uid: string): Promise<UserProfile | null> => {
    const userProfile = {uid: uid} as UserProfile

    const authUser = currentUser()

    if (authUser && authUser.uid === uid) {
        userProfile.uid = authUser.uid
        userProfile.displayName = authUser.displayName || ''
        userProfile.email = authUser.email || ''
        userProfile.emailVerified = authUser.emailVerified || false
        userProfile.photoURL = authUser.photoURL || ''
    }

    const firestoreUserProfile = await getUserProfile(uid)
    if (firestoreUserProfile) {
        //If the displayName or photoUrl are not set then use the FirestoreUserProfile values
        userProfile.displayName = !userProfile.displayName ? firestoreUserProfile.displayName : userProfile.displayName
        userProfile.photoURL = !userProfile.displayName ? firestoreUserProfile.photoURL : userProfile.photoURL

        //First name, surnmae and referral method are only stored in the FirestoreUserProfile
        userProfile.firstName = firestoreUserProfile.firstName
        userProfile.surname = firestoreUserProfile.surname
        userProfile.referralMethod = firestoreUserProfile.referralMethod
        userProfile.dateCreated = firestoreUserProfile.dateCreated
        userProfile.lastUpdated = firestoreUserProfile.lastUpdated
        userProfile.onboardEditScreen = firestoreUserProfile.onboardEditScreen
        userProfile.onboardDashboard = firestoreUserProfile.onboardDashboard
        userProfile.onboardPricing = firestoreUserProfile.onboardPricing
    }

    return userProfile
}

/**
 * Updates the AuthUser and FirestoreUserProfile with the values from the UserProfile object
 * @param user
 */
export function setCombinedUserProfile(user: UserProfile) {
    const authUser = currentUser()
    if (!authUser) {
        throw new Error("User is not signed in")
    }

    return updateProfile(authUser, {
        displayName: user.displayName,
        photoURL: user.photoURL
    }).then(() => {
        return updateIdentityPatch(authUser.uid, {
            displayName: user.displayName,
            photoURL: user.photoURL,
            email: user.email,
            referralMethod: user.referralMethod,
        }).then(() => {
            return updatePreferencesPatch(authUser.uid, {
                preferredLocale: user.preferredLocale,
            });
        }).then(() => user)
    })
}

export function updateIdentityPatch(uid: string, patch: IdentityPatch): Promise<void> {
    const payload = toFirestorePatch(patch);
    return updateDoc(doc(firestore, "UserProfiles", uid), payload);
}

export function updateOnboardingPatch(uid: string, patch: OnboardingPatch): Promise<void> {
    const payload = toFirestorePatch(patch);
    return updateDoc(doc(firestore, "UserProfiles", uid), payload);
}

export function updatePreferencesPatch(uid: string, patch: PreferencesPatch): Promise<void> {
    const payload = toFirestorePatch({
        ...(patch.preferredLocale !== undefined ? {preferredLocale: patch.preferredLocale} : {}),
        ...(patch.englishFlagSelection !== undefined ? {englishFlagSelection: patch.englishFlagSelection} : {}),
        ...(patch.favoriteSourceLanguages !== undefined
            ? {favoriteSourceLanguages: patch.favoriteSourceLanguages}
            : {}),
        ...(patch.favoriteSourceLanguage !== undefined
            ? {favoriteSourceLanguage: patch.favoriteSourceLanguage}
            : {}),
    } as Partial<UserProfile> & {
        favoriteSourceLanguage?: string | null;
        favoriteSourceLanguages?: string[] | null;
    });
    return updateDoc(doc(firestore, "UserProfiles", uid), payload);
}

/**
 * Updates the onboarding status for the user
 * @param uid
 * @param seen
 * @param attribute
 */
export function updateOnboardingStatus(uid: string, seen: boolean, attribute: string): Promise<void> {
    return updateDoc(doc(firestore, "UserProfiles", uid), {
        [attribute]: seen,
        lastUpdated: serverTimestamp(),
    }).then(() => {
        if (attribute === "onboardPricing" && seen) {
            rememberEnsureOnboarding(uid, {pricing: true});
        } else {
            clearEnsureUserProfileCache(uid);
        }
        console.log(`Updated userProfile ${attribute} status for user`, uid)
    })
}

export function updateTermsAccepted(uid: string, accepted: boolean): Promise<void> {
    return updateOnboardingPatch(uid, {
        youtubeTermsAccepted: accepted,
    }).then(() => {
        console.log(`Updated userProfile youtuberTermsAccepted status for user`, uid)
    })
}

export function updateEnglishFlagSelection(uid: string, countryCode: string): Promise<void> {
    return updateDoc(doc(firestore, "UserProfiles", uid), {
        englishFlagSelection: countryCode,
        lastUpdated: serverTimestamp(),
    }).then(() => {
        console.log(`Updated userProfile englishFlagSelection status for user`, uid)
    })
}

export async function getEnglishFlagSelection(uid: string): Promise<string | null> {
    try {
        return await withFirestoreAuthRetry(uid, async () => {
            const snapshot = await getDoc(doc(firestore, "UserProfiles", uid));
            if (!snapshot.exists()) {
                return null;
            }
            return snapshot.data()?.englishFlagSelection || null;
        }, {getCurrentUser: currentUser});
    } catch (error) {
        console.error("Error getting user profile:", error);
        throw error;
    }
}

export function updatePreferredLocale(uid: string, locale: string): Promise<void> {
    return updatePreferencesPatch(uid, {
        preferredLocale: locale,
    }).then(() => {
        console.log(`Updated userProfile preferredLocale to ${locale} for user`, uid)
    })
}

export function updateFavoriteSourceLanguage(uid: string, languageCode: string | null): Promise<void> {
    return updateFavoriteSourceLanguages(uid, languageCode ? [languageCode] : []);
}

export function updateFavoriteSourceLanguages(uid: string, languageCodes: string[]): Promise<void> {
    return updatePreferencesPatch(uid, {
        favoriteSourceLanguages: languageCodes,
    }).then(() => {
        console.log(`Updated userProfile favoriteSourceLanguages to [${languageCodes.join(", ")}] for user`, uid);
    });
}

export function updateUserEmail(uid: string, email: string): Promise<void> {
    return updateDoc(doc(firestore, "UserProfiles", uid), {
        email: email,
        lastUpdated: serverTimestamp(),
    }).then(() => {
        console.log(`Updated userProfile email to ${email} for user`, uid)
    })
}

/**
 * Check if an email address is already in use by another user profile in Firestore
 * @param email
 */
export async function isEmailInUse(email: string): Promise<boolean> {
    const q = query(collection(firestore, 'UserProfiles'), where('email', '==', email.trim().toLowerCase()));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
}

/**
 * Records a visit date (YYYY-MM-DD ISO string) for the user in Firestore,
 * using arrayUnion so concurrent writes are safe and duplicates are avoided.
 */
export function recordVisitDate(uid: string, date: string): Promise<void> {
    return updateDoc(doc(firestore, 'UserProfiles', uid), {
        visitDates: arrayUnion(date),
        lastUpdated: serverTimestamp(),
    });
}

/**
 * Updates the discount offer status for the user, used to prevent re-offering.
 */
export function updateDiscountOfferStatus(
    uid: string,
    status: 'offered' | 'accepted' | 'dismissed'
): Promise<void> {
    return updateDoc(doc(firestore, 'UserProfiles', uid), {
        discountOfferStatus: status,
        ...(status === 'accepted' ? { discountOfferAcceptedAt: serverTimestamp() } : {}),
        lastUpdated: serverTimestamp(),
    });
}